import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { obtenerProveedores, guardarProveedor, eliminarProveedor } from '../../services/comprasServicio';

const EMPTY_PROVEEDOR = { nombre: '', rut: '', contacto_nombre: '', telefono: '', email: '', direccion: '' };

// --- COMPONENTE: MODAL DE FORMULARIO ---
const ProviderFormModal = ({ isVisible, onClose, onSave, providerToEdit }) => {
    const [provider, setProvider] = useState(EMPTY_PROVEEDOR);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isVisible) setProvider(providerToEdit || EMPTY_PROVEEDOR);
    }, [providerToEdit, isVisible]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!provider.nombre) return toast.warning("Debes asignar un nombre");
        setIsLoading(true);
        try {
            await guardarProveedor(provider);
            onSave();
        } catch (err) {
            toast.error("Error al procesar el proveedor");
        } finally {
            setIsLoading(false);
        }
    };

    if (!isVisible) return null;

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content border-0 shadow-2xl rounded-xl overflow-hidden">
                    <div className="modal-header bg-white border-0 pt-4 px-4">
                        <h5 className="font-weight-bold m-0">{providerToEdit ? 'Editar Proveedor' : 'Nuevo Proveedor'}</h5>
                        <button className="close" onClick={onClose}>&times;</button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body p-4">
                            <div className="row">
                                <div className="col-md-6 form-group mb-3">
                                    <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Nombre / Razón Social</label>
                                    <input
                                        type="text" className="form-control form-control-lg border-light bg-light"
                                        value={provider.nombre} onChange={(e) => setProvider({ ...provider, nombre: e.target.value })}
                                        placeholder="Ej. Distribuidora Los Andes"
                                    />
                                </div>
                                <div className="col-md-6 form-group mb-3">
                                    <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">RUT</label>
                                    <input
                                        type="text" className="form-control form-control-lg border-light bg-light"
                                        value={provider.rut || ''} onChange={(e) => setProvider({ ...provider, rut: e.target.value })}
                                        placeholder="Ej. 76.123.456-7"
                                    />
                                </div>
                                <div className="col-md-6 form-group mb-3">
                                    <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Contacto</label>
                                    <input
                                        type="text" className="form-control form-control-lg border-light bg-light"
                                        value={provider.contacto_nombre || ''} onChange={(e) => setProvider({ ...provider, contacto_nombre: e.target.value })}
                                        placeholder="Nombre de contacto"
                                    />
                                </div>
                                <div className="col-md-6 form-group mb-3">
                                    <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Teléfono</label>
                                    <input
                                        type="text" className="form-control form-control-lg border-light bg-light"
                                        value={provider.telefono || ''} onChange={(e) => setProvider({ ...provider, telefono: e.target.value })}
                                    />
                                </div>
                                <div className="col-md-6 form-group mb-3">
                                    <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Email</label>
                                    <input
                                        type="email" className="form-control form-control-lg border-light bg-light"
                                        value={provider.email || ''} onChange={(e) => setProvider({ ...provider, email: e.target.value })}
                                    />
                                </div>
                                <div className="col-md-6 form-group mb-3">
                                    <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Dirección</label>
                                    <input
                                        type="text" className="form-control form-control-lg border-light bg-light"
                                        value={provider.direccion || ''} onChange={(e) => setProvider({ ...provider, direccion: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer border-0 bg-white p-4">
                            <button type="button" className="btn btn-link text-muted" onClick={onClose}>Cancelar</button>
                            <button type="submit" className="btn btn-primary btn-lg rounded-pill px-5 shadow-sm" disabled={isLoading}>
                                {isLoading ? 'Guardando...' : 'Guardar Proveedor'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// --- COMPONENTE PRINCIPAL ---
const Proveedores = () => {
    const [providers, setProviders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [providerToEdit, setProviderToEdit] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => { cargarProveedores(); }, []);

    const cargarProveedores = async () => {
        setIsLoading(true);
        try {
            const data = await obtenerProveedores();
            setProviders(data || []);
        } catch (err) {
            toast.error("Error de conexión al cargar proveedores");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id, nombre) => {
        if (!window.confirm(`¿Eliminar el proveedor "${nombre}"?`)) return;
        try {
            await eliminarProveedor(id);
            toast.success("Proveedor eliminado");
            cargarProveedores();
        } catch (err) {
            toast.error("No se pudo eliminar");
        }
    };

    const handleSave = () => {
        setIsModalOpen(false);
        setProviderToEdit(null);
        toast.success("¡Proveedor guardado!");
        cargarProveedores();
    };

    const filtered = providers.filter(p => (p.nombre || '').toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <div className="bg-light min-vh-100 py-5 px-3">
            <ToastContainer position="bottom-center" autoClose={2500} hideProgressBar />

            <div className="container" style={{ maxWidth: '1000px' }}>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-5">
                    <div>
                        <h1 className="h2 font-weight-bold text-dark mb-1">Proveedores</h1>
                        <p className="text-muted">Administra los proveedores de tus insumos y productos.</p>
                    </div>
                    <button className="btn btn-primary btn-lg rounded-pill shadow-lg px-4" onClick={() => { setProviderToEdit(null); setIsModalOpen(true); }}>
                        <i className="fas fa-plus mr-2"></i> Nuevo Proveedor
                    </button>
                </div>

                <div className="card border-0 shadow-sm rounded-xl overflow-hidden">
                    <div className="card-header bg-white border-0 pt-4 px-4">
                        <div className="input-group bg-light rounded-pill px-3 py-1 border">
                            <div className="input-group-prepend border-0 bg-transparent">
                                <span className="input-group-text bg-transparent border-0 text-muted">
                                    <i className="fas fa-search"></i>
                                </span>
                            </div>
                            <input
                                type="text" className="form-control bg-transparent border-0"
                                placeholder="Buscar proveedor..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="card-body p-0">
                        <div className="table-responsive" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            <table className="table table-hover align-middle mb-0">
                                <thead className="bg-light sticky-top" style={{ zIndex: 10 }}>
                                    <tr>
                                        <th className="border-0 px-4 py-3 text-muted small text-uppercase">Nombre</th>
                                        <th className="border-0 py-3 text-muted small text-uppercase">RUT</th>
                                        <th className="border-0 py-3 text-muted small text-uppercase">Contacto</th>
                                        <th className="border-0 px-4 py-3 text-right text-muted small text-uppercase">Opciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr><td colSpan="4" className="text-center py-5">Cargando proveedores...</td></tr>
                                    ) : (
                                        filtered.map(p => (
                                            <tr key={p.id}>
                                                <td className="px-4 py-3 font-weight-bold text-dark">{p.nombre}</td>
                                                <td className="py-3 text-muted">{p.rut || '-'}</td>
                                                <td className="py-3 text-muted">
                                                    {p.contacto_nombre || '-'}
                                                    {p.telefono ? ` · ${p.telefono}` : ''}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <button className="btn btn-icon btn-light rounded-circle mr-2" onClick={() => { setProviderToEdit(p); setIsModalOpen(true); }}>
                                                        <i className="fas fa-pen text-primary"></i>
                                                    </button>
                                                    <button className="btn btn-icon btn-light rounded-circle" onClick={() => handleDelete(p.id, p.nombre)}>
                                                        <i className="fas fa-trash text-danger"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                            {!isLoading && filtered.length === 0 && (
                                <div className="text-center py-5 text-muted">No hay proveedores registrados todavía.</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .rounded-xl { border-radius: 1.25rem !important; }
                .btn-icon { width: 38px; height: 38px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border: none; }
                .btn-icon:hover { background-color: #e9ecef; }
            `}</style>

            <ProviderFormModal
                isVisible={isModalOpen} onClose={() => setIsModalOpen(false)}
                onSave={handleSave} providerToEdit={providerToEdit}
            />
        </div>
    );
};

export default Proveedores;
